import { track, type Properties } from "@connect/observability";
import type { PostMessageClient } from "@connect/post-message/client";
import type { ConnectMessageMap } from "@connect/post-message/protocol";
import {
  type ActiveConnection,
  type Connection,
  type ConnectionError,
  ConnectionErrorCode,
  ConnectionStatus,
  type FailedConnection,
  type PendingConnection,
  type UnavailableConnection,
} from "@moonpay/platform-protocol/models/connection";
import { flushTerminal } from "../../lib/flush-terminal.ts";
import { trackFrameReady } from "../../lib/track-frame-ready.ts";
import type { ActiveMessageArgs, MessageMeta } from "../../lib/external-messages.types.ts";

type OnboardingCompletion = Connection;

export function createExternalMessages(send: PostMessageClient<ConnectMessageMap>["send"]) {
  let terminalSent = false;
  let closeStarted = false;
  let terminalDelivery: Promise<void> | null = null;
  const complete = async (payload: OnboardingCompletion, meta: MessageMeta) => {
    terminalSent = true;
    track.info("connect.onboarding.complete", { connectionStatus: payload.status, ...meta });
    terminalDelivery = flushTerminal();
    await terminalDelivery;
    send("complete", payload);
  };

  const fail = async (payload: ConnectionError, meta: MessageMeta) => {
    terminalSent = true;
    track.error("connect.onboarding.error", { errorCode: payload.code, ...meta });
    terminalDelivery = flushTerminal();
    await terminalDelivery;
    send("error", payload);
  };

  return {
    ready(meta: MessageMeta = {}) {
      track.info("connect.onboarding.ready", meta);
      trackFrameReady("onboarding");
      send("ready");
    },

    active({ credentials, capabilities, customerId, meta = {} }: ActiveMessageArgs) {
      const geo =
        capabilities.ramps?.requirements.paymentDisclosures ??
        capabilities.guestCheckout?.requirements.paymentDisclosures;

      const payload: ActiveConnection = {
        status: ConnectionStatus.active,
        customer: {
          id: customerId,
          ...(geo?.country !== undefined && { country: geo.country }),
          ...(geo?.administrativeArea !== undefined && {
            administrativeArea: geo.administrativeArea,
          }),
          ...(geo?.area !== undefined && { area: geo.area }),
        },
        credentials,
        capabilities,
      };

      return complete(payload, meta);
    },

    pending(meta: MessageMeta = {}) {
      const payload: PendingConnection = { status: ConnectionStatus.pending };
      return complete(payload, meta);
    },

    unavailable(meta: MessageMeta = {}) {
      const payload: UnavailableConnection = { status: ConnectionStatus.unavailable };
      return complete(payload, meta);
    },

    failed(reason: string, meta: MessageMeta = {}) {
      const payload: FailedConnection = { status: ConnectionStatus.failed, reason };
      return complete(payload, meta);
    },

    connectionFailedGeneric(message: string, meta: MessageMeta = {}) {
      const payload: ConnectionError = {
        code: ConnectionErrorCode.connectionFailedGeneric,
        message,
      };
      return fail(payload, meta);
    },

    async close(meta: Properties = {}) {
      if (closeStarted) {
        return;
      }
      closeStarted = true;
      if (!terminalSent) {
        track.info("connect.onboarding.close", meta);
        await flushTerminal();
      } else if (terminalDelivery) {
        await terminalDelivery;
      }
      send("close");
    },
  };
}
